import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '../messages'
import {
  BlueSystemSysIds,
  ScheduleLineStatusType,
  OrderStatusType,
} from '@src/types/graphql-types'
import { OrderItemFragment } from '@src/queries/OrderDetailsQuery.generated'
import { useRouter } from 'next/router'
import { omit } from 'lodash'

export enum OrderType {
  Emerald = 'emerald',
  Iscala = 'iscala',
  Phoenix = 'phoenix',
}

export const checkIfDropShip = (status: string) => {
  switch (status) {
    case ScheduleLineStatusType.CompletedDropShip:
    case ScheduleLineStatusType.CancelledDropShip:
      return true
    default:
      return false
  }
}
export const isItBlueOrder = (sysId?: string) => {
  switch (sysId) {
    case BlueSystemSysIds.Orc:
    case BlueSystemSysIds.TempoEu:
    case BlueSystemSysIds.TempoLa:
    case BlueSystemSysIds.TempoNa:
    case BlueSystemSysIds.Emerald:
    case BlueSystemSysIds.Iscala:
      return true
    default:
      return false
  }
}

const orderItemsTableStatusMapping = {
  [ScheduleLineStatusType.Open]: 'ESTIMATED_TO_SHIP_ON',
  [ScheduleLineStatusType.Empty]: 'ESTIMATED_TO_SHIP_ON',
  [ScheduleLineStatusType.Pending]: 'ESTIMATED_TO_SHIP_ON',
  [ScheduleLineStatusType.PendingApproval]: 'ESTIMATED_TO_SHIP_ON',
  [ScheduleLineStatusType.PendingReview]: 'ESTIMATED_TO_SHIP_ON',
  [ScheduleLineStatusType.PendingDropShip]: 'ESTIMATED_TO_SHIP_ON',
  [ScheduleLineStatusType.InProcess]: 'ESTIMATED_TO_SHIP_ON',
  [ScheduleLineStatusType.Processing]: 'ESTIMATED_TO_SHIP_ON',
  [ScheduleLineStatusType.PartiallyCancelled]: 'ESTIMATED_TO_SHIP_ON',
  [ScheduleLineStatusType.Delayed]: 'ESTIMATED_TO_SHIP_ON',
  [ScheduleLineStatusType.Shipped]: 'SHIPPED_ON',
  [ScheduleLineStatusType.Completed]: 'SHIPPED_ON',
  [ScheduleLineStatusType.Invoiced]: 'SHIPPED_ON',
  [ScheduleLineStatusType.CompletedDropShip]: 'SHIPPED_ON',
  [ScheduleLineStatusType.Closed]: 'SHIPPED_ON',
  [ScheduleLineStatusType.PartiallyReceived]: 'SHIPPED_ON',
  [ScheduleLineStatusType.PartiallyShipped]: 'SHIPPED_ON',
  [ScheduleLineStatusType.Received]: 'RECEIVED',
  [ScheduleLineStatusType.ShippingInProgress]: 'SHIPPED_ON',
  [ScheduleLineStatusType.Cancelled]: 'WIMS_ORDER_ITEM_SHIP_STATUS_CANCELLED',
  [ScheduleLineStatusType.Rejected]: 'WIMS_ORDER_ITEM_SHIP_STATUS_CANCELLED',
  [ScheduleLineStatusType.Refunded]: 'WIMS_ORDER_ITEM_SHIP_STATUS_REFUNDED',
  [ScheduleLineStatusType.PendingRefund]:
    'WIMS_ORDER_ITEM_SHIP_STATUS_PENDING_REFUND',
  [ScheduleLineStatusType.CancelledDropShip]:
    'WIMS_ORDER_ITEM_SHIP_STATUS_CANCELLED',
  WIMS_ORDER_ITEM_SHIP_STATUS_H: 'WIMS_ORDER_STATUS_H',
  [ScheduleLineStatusType.FulfilmentDeliveryDelayed]:
    'FULFILMENT_DELIVERY_DELAYED',
  [ScheduleLineStatusType.ContactForAvailability]: 'CONTACT_FOR_AVAILABILITY',
} as const

type ScheduleLineStatus = keyof typeof orderItemsTableStatusMapping
export type OrderItemsTableStatusKey =
  (typeof orderItemsTableStatusMapping)[ScheduleLineStatus]

// Provides for short (1-3 word) descriptions of the current shipping status to be used in chip elements
const orderItemsChipStatusMapping = {
  [ScheduleLineStatusType.Open]: 'IN_PROGRESS',
  [ScheduleLineStatusType.Empty]: 'IN_PROGRESS',
  [ScheduleLineStatusType.Pending]: 'IN_PROGRESS',
  [ScheduleLineStatusType.PendingApproval]: 'IN_PROGRESS',
  [ScheduleLineStatusType.PendingReview]: 'IN_PROGRESS',
  [ScheduleLineStatusType.PendingDropShip]: 'IN_PROGRESS',
  [ScheduleLineStatusType.InProcess]: 'IN_PROGRESS',
  [ScheduleLineStatusType.Processing]: 'IN_PROGRESS',
  [ScheduleLineStatusType.PartiallyCancelled]: 'IN_PROGRESS',
  [ScheduleLineStatusType.Delayed]: 'DELAYED',
  [ScheduleLineStatusType.Shipped]: 'SHIPPED',
  [ScheduleLineStatusType.Completed]: 'SHIPPED',
  [ScheduleLineStatusType.Invoiced]: 'SHIPPED',
  [ScheduleLineStatusType.CompletedDropShip]: 'SHIPPED',
  [ScheduleLineStatusType.Closed]: 'SHIPPED',
  [ScheduleLineStatusType.PartiallyReceived]: 'SHIPPED',
  [ScheduleLineStatusType.PartiallyShipped]: 'SHIPPED',
  [ScheduleLineStatusType.Received]: 'RECEIVED',
  [ScheduleLineStatusType.ShippingInProgress]: 'SHIPPED',
  [ScheduleLineStatusType.Cancelled]: 'CANCELLED',
  [ScheduleLineStatusType.Rejected]: 'CANCELLED',
  [ScheduleLineStatusType.Refunded]: 'TRANSACTION_TYPE_REFUND',
  [ScheduleLineStatusType.PendingRefund]: 'PENDING',
  [ScheduleLineStatusType.CancelledDropShip]: 'CANCELLED',
  WIMS_ORDER_ITEM_SHIP_STATUS_H: 'WIMS_ORDER_STATUS_H',
  [ScheduleLineStatusType.FulfilmentDeliveryDelayed]: 'DELAYED',
} as const

type OrderItemChipStatus = keyof typeof orderItemsChipStatusMapping
export type OrderItemChipStatusKey =
  (typeof orderItemsChipStatusMapping)[OrderItemChipStatus]

// Shipping status labels differ based upon target UI elements
export type OrderItemStatusTarget = 'table' | 'chip'

export const getOrderItemsStatusKey = (
  status: string,
  target: OrderItemStatusTarget = 'table'
): OrderItemsTableStatusKey | OrderItemChipStatusKey | null => {
  return (
    (target === 'chip'
      ? orderItemsChipStatusMapping[status]
      : orderItemsTableStatusMapping[status]) || null
  )
}

export const renderOrderItemsStatus = (
  status: string,
  target: OrderItemStatusTarget = 'table'
) => {
  const tableStatusKey = getOrderItemsStatusKey(status, target)
  const statusMessage = tableStatusKey && messages[tableStatusKey]
  if (!statusMessage) return null
  return <FormattedMessage {...statusMessage} />
}

export const getModalOrderItemStatusPending = (
  modalOrderItem: OrderItemFragment | null
) => {
  return (
    modalOrderItem !== null &&
    (modalOrderItem.status === OrderStatusType.Pending ||
      modalOrderItem.status === OrderStatusType.PendingReview)
  )
}

enum AlertType {
  ContactUsSubmitted = 'contactUsSubmitted',
  CancelSuccess = 'cancelSuccess',
}

export const useOrderDetailQueryAlert = () => {
  const intl = useIntl()
  const { query, pathname, replace } = useRouter<AlertType>()

  const alertType = query.cancelSuccess
    ? AlertType.CancelSuccess
    : query.contactUsSubmitted
      ? AlertType.ContactUsSubmitted
      : null

  const dismissAlert = () => {
    if (alertType) {
      replace(
        {
          pathname: pathname,
          query: omit(query, alertType),
        },
        undefined,
        {
          shallow: true,
        }
      )
    }
  }

  const getMessage = () => {
    if (alertType === AlertType.CancelSuccess) {
      return intl.formatMessage({
        id: 'ORDER_CANCEL_SUCCESS',
        defaultMessage:
          'Your return request has been successfully submitted. You will be notified once we confirm your request.',
      })
    } else if (alertType === AlertType.ContactUsSubmitted) {
      return intl.formatMessage({
        id: 'CONTACT_US_SUBMITTED',
        defaultMessage:
          'Thank you. We have received your message. We will reply by email as soon as possible.',
      })
    } else {
      return ''
    }
  }

  return {
    message: getMessage(),
    dismissAlert,
  }
}
